import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Camera, X, Package, ImageIcon } from "lucide-react";
import { PhotoLightbox, useLightbox } from "@/components/photo-lightbox";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";

export const PHOTO_CATEGORIES = [
  { key: "all", label: "All Photos", icon: "📷" },
  { key: "kitchen", label: "Kitchen", icon: "🍳" },
  { key: "living_room", label: "Living Room", icon: "🛋️" },
  { key: "bedrooms", label: "Bedrooms", icon: "🛏️" },
  { key: "bathrooms", label: "Bathrooms", icon: "🚿" },
  { key: "floor", label: "Floor", icon: "🧹" },
  { key: "fridge", label: "Fridge", icon: "❄️" },
];

const inventoryConditionConfig: Record<string, { label: string; variant: "default" | "secondary" | "destructive" | "outline" }> = {
  good: { label: "Good", variant: "default" },
  damaged: { label: "Damaged", variant: "destructive" },
  needs_replacement: { label: "Replace", variant: "destructive" },
  missing: { label: "Missing", variant: "outline" },
};

const invZoneLabels: Record<string, string> = {
  kitchen: "Kitchen", bathroom: "Bathroom", bedroom: "Bedroom",
  living_room: "Living Room", dining_room: "Dining Room", balcony: "Balcony",
  laundry: "Laundry", hallway: "Hallway", other: "Other",
};

export function CleaningPhotoUploader({ jobId, apartmentId, categories, uploadingPhoto, uploadingCategory, uploadingBulk, onUpload, onBulkUpload, onDelete, onComplete, isPending }: {
  jobId: number;
  apartmentId: number | null;
  categories: { key: string; label: string; icon: string }[];
  uploadingPhoto: boolean;
  uploadingCategory: string | null;
  uploadingBulk: boolean;
  onUpload: (jobId: number, category: string, file: File) => void;
  onBulkUpload: (jobId: number, files: File[]) => void;
  onDelete: (jobId: number, photoId: number) => void;
  onComplete: () => void;
  isPending: boolean;
}) {
  const { data: photos = [] } = useQuery<any[]>({
    queryKey: ["/api/cleaning-jobs", jobId, "photos"],
  });
  const { lightboxState, openLightbox, closeLightbox } = useLightbox();

  const photosByCategory = categories.reduce((acc, cat) => {
    acc[cat.key] = (photos as any[]).filter(p => p.category === cat.key);
    return acc;
  }, {} as Record<string, any[] | undefined>);

  const totalPhotos = photos.length;
  const allPhotos = categories.flatMap(cat =>
    (photosByCategory[cat.key] ?? []).map((p: any) => ({ id: p.id, url: p.url, label: `${cat.icon} ${cat.label}` }))
  );
  const getGlobalIndex = (photo: any) => allPhotos.findIndex(p => p.id === photo.id);
  const roomCategories = categories.filter(cat => cat.key !== "all");
  const allCategory = categories.find(cat => cat.key === "all");
  const allCategoryPhotos = photosByCategory["all"] ?? [];

  return (
    <div className="space-y-3">
      <Label className="text-sm font-medium flex items-center gap-1">
        <Camera className="w-4 h-4" /> Cleaning Photos by Area
      </Label>

      {allCategory && (
        <div className="border-2 border-primary/30 rounded-lg p-2 space-y-1.5 bg-primary/5">
          <div className="flex items-center justify-between">
            <span className="text-xs font-medium flex items-center gap-1">
              <span>{allCategory.icon}</span> {allCategory.label}
              {allCategoryPhotos.length > 0 && (
                <Badge variant="secondary" className="text-[10px] px-1 h-4">{allCategoryPhotos.length}</Badge>
              )}
              <span className="text-[10px] text-muted-foreground font-normal">(select multiple)</span>
            </span>
            <label className="cursor-pointer">
              <input
                type="file"
                accept="image/*"
                multiple
                className="hidden"
                onChange={(e) => {
                  const files = Array.from(e.target.files || []);
                  if (files.length > 0) onBulkUpload(jobId, files);
                  e.target.value = "";
                }}
                disabled={uploadingBulk || uploadingPhoto}
              />
              <span className="text-xs text-primary flex items-center gap-0.5 hover:underline font-medium">
                <Camera className="w-3 h-3" />
                {uploadingBulk ? "Uploading..." : "Add Photos"}
              </span>
            </label>
          </div>
          {allCategoryPhotos.length > 0 && (
            <div className="flex gap-1.5 flex-wrap">
              {allCategoryPhotos.map((photo: any) => (
                <div key={photo.id} className="relative group">
                  <img
                    src={photo.url}
                    alt="photo"
                    className="w-14 h-14 rounded object-cover cursor-pointer"
                    onClick={() => openLightbox(allPhotos, getGlobalIndex(photo))}
                  />
                  <Button
                    size="icon"
                    variant="destructive"
                    className="absolute -top-1 -right-1 h-4 w-4 rounded-full p-0 opacity-0 group-hover:opacity-100 transition-opacity"
                    onClick={() => onDelete(jobId, photo.id)}
                  >
                    <X className="w-2.5 h-2.5" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <div className="space-y-2">
        {roomCategories.map(cat => (
          <div key={cat.key} className="border rounded-lg p-2 space-y-1.5">
            <div className="flex items-center justify-between">
              <span className="text-xs font-medium flex items-center gap-1">
                <span>{cat.icon}</span> {cat.label}
                {(photosByCategory[cat.key]?.length ?? 0) > 0 && (
                  <Badge variant="secondary" className="text-[10px] px-1 h-4">{photosByCategory[cat.key]?.length}</Badge>
                )}
              </span>
              <label className="cursor-pointer">
                <input
                  type="file"
                  accept="image/*"
                  capture="environment"
                  className="hidden"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) onUpload(jobId, cat.key, file);
                    e.target.value = "";
                  }}
                  disabled={uploadingPhoto || uploadingBulk}
                />
                <span className="text-xs text-primary flex items-center gap-0.5 hover:underline">
                  <Camera className="w-3 h-3" />
                  {uploadingPhoto && uploadingCategory === cat.key ? "Uploading..." : "Add"}
                </span>
              </label>
            </div>
            {(photosByCategory[cat.key]?.length ?? 0) > 0 && (
              <div className="flex gap-1.5 flex-wrap">
                {(photosByCategory[cat.key] ?? []).map((photo: any) => (
                  <div key={photo.id} className="relative group">
                    <img
                      src={photo.url}
                      alt={cat.label}
                      className="w-14 h-14 rounded object-cover cursor-pointer"
                      onClick={() => openLightbox(allPhotos, getGlobalIndex(photo))}
                    />
                    <Button
                      size="icon"
                      variant="destructive"
                      className="absolute -top-1 -right-1 h-4 w-4 rounded-full p-0 opacity-0 group-hover:opacity-100 transition-opacity"
                      onClick={() => onDelete(jobId, photo.id)}
                    >
                      <X className="w-2.5 h-2.5" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
      <InventoryCheckStep jobId={jobId} apartmentId={apartmentId} onComplete={onComplete} isPending={isPending} totalPhotos={totalPhotos} />
      {lightboxState && (
        <PhotoLightbox photos={lightboxState.photos} initialIndex={lightboxState.index} onClose={closeLightbox} />
      )}
    </div>
  );
}

export function InventoryCheckStep({ jobId, apartmentId, onComplete, isPending, totalPhotos }: {
  jobId: number;
  apartmentId: number | null;
  onComplete: () => void;
  isPending: boolean;
  totalPhotos: number;
}) {
  const [showInventory, setShowInventory] = useState(false);
  const [itemConditions, setItemConditions] = useState<Record<number, { condition: string; notes: string }>>({});
  const qc = useQueryClient();
  const { toast } = useToast();

  const { data: inventoryItems = [] } = useQuery<any[]>({
    queryKey: ["/api/cleaner/inventory", apartmentId],
    enabled: showInventory,
  });

  const submitReport = useMutation({
    mutationFn: async () => {
      const items = (inventoryItems as any[]).map((item: any) => ({
        inventoryItemId: item.id,
        templateName: item.templateName || "Unknown",
        zone: item.zone,
        condition: itemConditions[item.id]?.condition || item.condition || "good",
        notes: itemConditions[item.id]?.notes || undefined,
      }));
      await apiRequest("POST", "/api/cleaner/inventory-report", { apartmentId, cleaningJobId: jobId, items });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["/api/admin/inventory-reports"] });
      toast({ title: "Inventory report submitted" });
      setShowInventory(false);
      onComplete();
    },
    onError: (e: any) => toast({ title: "Error", description: e.message, variant: "destructive" }),
  });

  const groupedByZone = (inventoryItems as any[]).reduce((acc: Record<string, any[]>, item: any) => {
    const zone = item.zone || "other";
    if (!acc[zone]) acc[zone] = [];
    acc[zone].push(item);
    return acc;
  }, {});

  if (!showInventory) {
    return (
      <div className="space-y-2">
        <Button variant="outline" className="w-full" onClick={() => setShowInventory(true)} disabled={isPending || totalPhotos === 0}>
          <Package className="w-4 h-4 mr-1" /> Inventory Check (Optional)
        </Button>
        <Button className="w-full" onClick={onComplete} disabled={isPending || totalPhotos === 0}>
          <X className="w-4 h-4 mr-1" /> Mark Complete {totalPhotos > 0 ? `(${totalPhotos} photos)` : ""}
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-3 border rounded-lg p-3 bg-muted/30">
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium flex items-center gap-1"><Package className="w-4 h-4" /> Inventory Check</span>
        <Button variant="ghost" size="sm" onClick={() => setShowInventory(false)}><X className="w-4 h-4" /></Button>
      </div>
      {(inventoryItems as any[]).length === 0 ? (
        <p className="text-xs text-muted-foreground text-center py-2">No inventory items for this apartment.</p>
      ) : (
        <div className="space-y-3">
          {Object.entries(groupedByZone).map(([zone, items]) => (
            <div key={zone}>
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1">{invZoneLabels[zone] || zone}</p>
              <div className="space-y-1.5">
                {(items as any[]).map((item: any) => {
                  const currentCondition = itemConditions[item.id]?.condition || item.condition || "good";
                  return (
                    <div key={item.id} className="border rounded p-2 space-y-1">
                      <div className="flex items-center justify-between">
                        <span className="text-xs font-medium">{item.templateName} <span className="text-muted-foreground">x{item.quantity}</span></span>
                      </div>
                      <div className="flex gap-1 flex-wrap">
                        {(["good", "damaged", "needs_replacement", "missing"] as const).map(cond => (
                          <Button
                            key={cond}
                            variant={currentCondition === cond ? "default" : "outline"}
                            size="sm"
                            className="h-6 text-[10px] px-2"
                            onClick={() => setItemConditions(prev => ({
                              ...prev,
                              [item.id]: { ...prev[item.id], condition: cond, notes: prev[item.id]?.notes || "" },
                            }))}
                          >
                            {inventoryConditionConfig[cond]?.label}
                          </Button>
                        ))}
                      </div>
                      {currentCondition !== "good" && (
                        <Input
                          placeholder="Add notes..."
                          className="h-7 text-xs"
                          value={itemConditions[item.id]?.notes || ""}
                          onChange={e => setItemConditions(prev => ({
                            ...prev,
                            [item.id]: { ...prev[item.id], condition: currentCondition, notes: e.target.value },
                          }))}
                        />
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
      <div className="space-y-2">
        <Button className="w-full" onClick={() => submitReport.mutate()} disabled={submitReport.isPending || (inventoryItems as any[]).length === 0}>
          <Package className="w-4 h-4 mr-1" />
          {submitReport.isPending ? "Submitting..." : "Submit Report & Complete"}
        </Button>
        <Button variant="outline" className="w-full" onClick={() => { setShowInventory(false); onComplete(); }} disabled={isPending}>
          Skip & Mark Complete
        </Button>
      </div>
    </div>
  );
}

export function CleaningPhotoViewer({ jobId, categories }: {
  jobId: number;
  categories: { key: string; label: string; icon: string }[];
}) {
  const { data: photos = [] } = useQuery<any[]>({
    queryKey: ["/api/cleaning-jobs", jobId, "photos"],
  });
  const { lightboxState, openLightbox, closeLightbox } = useLightbox();

  if (!photos || photos.length === 0) return null;

  const photosByCategory = categories.reduce((acc, cat) => {
    acc[cat.key] = (photos as any[]).filter(p => p.category === cat.key);
    return acc;
  }, {} as Record<string, any[] | undefined>);

  const hasPhotos = categories.some(cat => (photosByCategory[cat.key]?.length ?? 0) > 0);
  if (!hasPhotos) return null;

  const allPhotos = categories.flatMap(cat =>
    (photosByCategory[cat.key] ?? []).map((p: any) => ({ id: p.id, url: p.url, label: `${cat.icon} ${cat.label}` }))
  );
  const getGlobalIndex = (photo: any) => allPhotos.findIndex(p => p.id === photo.id);

  return (
    <div className="space-y-2">
      <Label className="text-xs text-muted-foreground flex items-center gap-1">
        <ImageIcon className="w-3 h-3" /> Cleaning Photos
      </Label>
      <div className="space-y-1.5">
        {categories.filter(cat => (photosByCategory[cat.key]?.length ?? 0) > 0).map(cat => (
          <div key={cat.key}>
            <span className="text-[10px] text-muted-foreground">{cat.icon} {cat.label}</span>
            <div className="flex gap-1 flex-wrap mt-0.5">
              {(photosByCategory[cat.key] ?? []).map((photo: any) => (
                <img
                  key={photo.id}
                  src={photo.url}
                  alt={cat.label}
                  className="w-14 h-14 rounded object-cover cursor-pointer hover:ring-2 ring-primary transition-all"
                  onClick={() => openLightbox(allPhotos, getGlobalIndex(photo))}
                />
              ))}
            </div>
          </div>
        ))}
      </div>
      {lightboxState && (
        <PhotoLightbox photos={lightboxState.photos} initialIndex={lightboxState.index} onClose={closeLightbox} />
      )}
    </div>
  );
}
